import { api } from '@/lib/api/client';
import { type PaginatedResponse, buildQuery } from '@/lib/api/types';
import { deleteFileApi, uploadFileApi } from '@api-client/files';
import { getOrganizationsApi } from '@api-client/organizations';
import type {
  OrgAdminRole,
  OrganizationAdminRequestStatus,
  StripeAccountStatus,
  SubPlan,
  SubscriptionStatus,
} from '@shared/enums/domain.enum';
import type {
  CreateOrganizationPayload,
  OrganizationAdminRequest,
  UpdateOrganizationPayload,
} from '@shared/types/entities';

export { getOrganizationsApi };
export type { GetOrganizationsParams, Organization } from '@api-client/organizations';

export async function getOrganizationApi(id: number): Promise<import('@shared/types/entities').Organization> {
  return api.get<import('@shared/types/entities').Organization>(`/organizations/${id}/admin`);
}

export async function createOrganizationApi(
  payload: CreateOrganizationPayload,
): Promise<import('@shared/types/entities').Organization> {
  return api.post<import('@shared/types/entities').Organization>('/organizations', payload);
}

export async function updateOrganizationApi(
  id: number,
  payload: UpdateOrganizationPayload,
): Promise<import('@shared/types/entities').Organization> {
  return api.patch<import('@shared/types/entities').Organization>(`/organizations/${id}`, payload);
}

export async function deleteOrganizationApi(id: number): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/organizations/${id}`);
}

/**
 * [SUPER_ADMIN] Active / désactive une organisation.
 *
 * Route dédiée (et non PATCH /organizations/:id) pour qu'un ORGANIZATION_ADMIN
 * ne puisse pas se réactiver lui-même. Désactiver masque la fiche publique, le
 * blog et les couvertures d'articles, et bloque la connexion de ses admins.
 */
export async function adminUpdateOrganizationStatusApi(
  id: number,
  isActive: boolean,
): Promise<import('@shared/types/entities').Organization> {
  return api.patch<import('@shared/types/entities').Organization>(`/organizations/${id}/admin-status`, { isActive });
}

// ─── Admins management (SUPER_ADMIN / ORGANIZATION_ADMIN) ──────────

export async function addOrgAdminApi(
  id: number,
  payload: { userId: number; role?: OrgAdminRole },
): Promise<import('@shared/types/entities').Organization> {
  return api.post(`/organizations/${id}/admins`, payload);
}

export async function removeOrgAdminApi(id: number, userId: number): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/organizations/${id}/admins/${userId}`);
}

// ─── Clubs rattachés ───────────────────────────────────────────────

export async function attachClubApi(id: number, clubId: number): Promise<{ message: string }> {
  return api.post<{ message: string }>(`/organizations/${id}/clubs/${clubId}`, {});
}

export async function detachClubApi(id: number, clubId: number): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/organizations/${id}/clubs/${clubId}`);
}

export async function uploadOrgLogoApi(
  orgId: number,
  file: File,
): Promise<{ id: number; key: string; mimeType: string }> {
  return uploadFileApi({ usage: 'ORG_LOGO', file, extraFields: { organizationId: String(orgId) } });
}

export async function uploadOrgCoverApi(
  orgId: number,
  file: File,
): Promise<{ id: number; key: string; mimeType: string }> {
  return uploadFileApi({ usage: 'ORG_COVER', file, extraFields: { organizationId: String(orgId) } });
}

export async function deleteOrgCoverApi(fileId: number): Promise<void> {
  return deleteFileApi(fileId);
}

export async function stripeConnectApi(id: number): Promise<{ url: string }> {
  return api.post(`/organizations/${id}/stripe/connect`, {});
}

export async function stripeStatusApi(id: number): Promise<{
  status: StripeAccountStatus;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
}> {
  return api.get(`/organizations/${id}/stripe/status`);
}

export async function stripeDashboardLinkApi(id: number): Promise<{ url: string }> {
  return api.post(`/organizations/${id}/stripe/dashboard-link`, {});
}

export async function stripeSetupIntentApi(id: number): Promise<{ clientSecret: string }> {
  return api.post(`/organizations/${id}/stripe/setup-intent`, {});
}

export async function stripeSubscribeApi(
  id: number,
  interval: 'monthly' | 'annual',
  paymentMethodId?: string,
): Promise<{ subscriptionId: string; status: string; clientSecret?: string }> {
  return api.post(`/organizations/${id}/stripe/subscribe`, { interval, ...(paymentMethodId && { paymentMethodId }) });
}

export async function stripeBillingPortalApi(id: number): Promise<{ portalUrl: string }> {
  return api.post(`/organizations/${id}/stripe/billing-portal`, {});
}

export async function createOrgAdminRequestApi(
  orgId: number,
  payload: { message?: string },
): Promise<{ id: number; uid: string }> {
  return api.post(`/organizations/${orgId}/admin-requests`, payload);
}

// ─── Admin requests ────────────────────────────────────────────────

export type GetAdminRequestsParams = {
  organizationId?: number;
  status?: OrganizationAdminRequestStatus;
  page?: number;
  limit?: number;
};

export async function getOrgAdminRequestsApi(
  params?: GetAdminRequestsParams,
): Promise<PaginatedResponse<OrganizationAdminRequest>> {
  return api.get(`/organizations/admin-requests${buildQuery(params as Record<string, unknown>)}`);
}

export async function updateOrgAdminRequestApi(
  requestId: number,
  payload: { status: OrganizationAdminRequestStatus; rejectedReason?: string },
): Promise<OrganizationAdminRequest> {
  return api.patch(`/organizations/admin-requests/${requestId}`, payload);
}

export async function pingOrgAdminsApi(
  orgId: number,
  payload: { subject: string; message: string; contextRequestId?: number },
): Promise<{ sent: number }> {
  return api.post(`/organizations/${orgId}/ping-admins`, payload);
}

// ─── Capabilities (quotas Commission + statut Forfait) ──────────

export type OrganizationCapabilities = {
  organizationId: number;
  plan: SubPlan;
  subscriptionStatus: SubscriptionStatus;
  forfaitActive: boolean;
  commission: {
    monthlyUsed: number;
    yearlyUsed: number;
    monthlyLimit: number;
    yearlyLimit: number;
    monthlyRemaining: number;
    yearlyRemaining: number;
    /** Commissions réellement encaissées ce mois (cents, source Stripe). */
    commissionThisMonthCents: number;
    canCreatePaidCompetition: boolean;
    period: {
      monthStart: string;
      monthEnd: string;
      yearStart: string;
      yearEnd: string;
      timeZone: string;
    };
  };
};

export async function getOrganizationCapabilitiesApi(orgId: number): Promise<OrganizationCapabilities> {
  return api.get(`/competitions/organizations/${orgId}/capabilities`);
}
